/**
 * Turning pointer movement into estimator marks. Pure functions over page-normalised
 * points (0..1, origin top-left), the only coordinates a mark is ever stored in.
 */
import type { Shape } from "@/api/client";

export type Tool = Shape["kind"];
export type Point = [number, number];

/** The most points a freehand line keeps; the API takes 2000. */
const MAX_POINTS = 1500;

export function clampPoint([x, y]: readonly [number, number]): Point {
  return [Math.min(1, Math.max(0, x)), Math.min(1, Math.max(0, y))];
}

/** The points of a mark being drawn, after the pointer reached `p`. */
export function addPoint(tool: Tool, points: readonly Point[], p: Point, minStep: number): Point[] {
  const at = clampPoint(p);
  const first = points[0];
  if (!first) {
    return [at];
  }
  if (tool !== "freehand") {
    return tool === "text" ? [first] : [first, at];
  }
  const last = points[points.length - 1] ?? first;
  if (Math.hypot(at[0] - last[0], at[1] - last[1]) < minStep || points.length >= MAX_POINTS) {
    return [...points];
  }
  return [...points, at];
}

interface ShapeOptions {
  colour: string;
  pageId: string;
  /** Below this a drag is a click, and draws nothing. */
  minSpan: number;
  text?: string;
}

/** The finished mark, or null when the pointer did not draw one. */
export function shapeFrom(tool: Tool, points: readonly Point[], o: ShapeOptions): Shape | null {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) {
    return null;
  }
  const base = { kind: tool, page_id: o.pageId, colour: o.colour, text: null };
  if (tool === "text") {
    const text = (o.text ?? "").trim();
    return text ? { ...base, points: [first], text } : null;
  }
  const span =
    tool === "freehand" ? pathLength(points) : Math.hypot(last[0] - first[0], last[1] - first[1]);
  if (span < o.minSpan || points.length < 2) {
    return null;
  }
  return { ...base, points: tool === "freehand" ? [...points] : [first, last] };
}

function pathLength(points: readonly Point[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    total += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return total;
}

export function onlyPage(shapes: readonly Shape[], pageId: string): Shape[] {
  return shapes.filter((s) => s.page_id === pageId);
}

/** All marks, with one sheet's replaced by `next`; other sheets' are kept as they were. */
export function replacePage(
  shapes: readonly Shape[],
  pageId: string,
  next: readonly Shape[],
): Shape[] {
  return [...shapes.filter((s) => s.page_id !== pageId), ...next];
}
